<template>
  <section class="highlights-scene-section">
    <h3 class="highlights-scene-section__title">
      <i class="highlights-scene-section__title__icon">
        <svg
          width="19"
          height="19"
          viewBox="0 0 19 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_11934_284288)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.5 2.5C0.947715 2.5 0.5 2.94772 0.5 3.5V15.5C0.5 16.0523 0.947716 16.5 1.5 16.5H17.5C18.0523 16.5 18.5 16.0523 18.5 15.5V3.5C18.5 2.94772 18.0523 2.5 17.5 2.5H1.5ZM2.5 4.5H4.5V6.5H2.5V4.5ZM8.5 5.5L15.5 9.5L8.5 13.5V5.5ZM4.5 12.5H2.5V14.5H4.5V12.5ZM2.5 8.5H4.5V10.5H2.5V8.5Z"
              fill="#00DBA6"
            />
          </g>
          <defs>
            <clipPath id="clip0_11934_284288">
              <rect width="18" height="18" fill="white" transform="translate(0.5 0.5)" />
            </clipPath>
          </defs>
        </svg>
      </i>
      <span class="highlights-scene-section__title__text">
        {{ $tc('HomePage.highlights.title') }}
      </span>
    </h3>

    <carousel-list-wrapper-parts
      class="highlights-scene-section__scene-list"
      carouselListType="scene"
      :enabledCarouselList="enabledCarouselList"
    >
      <template v-for="highlightOrComment in newArrivalHighlightAndCommentData">
        <highlights-card-parts
          v-if="highlightOrComment.highlightCard instanceof HighlightDocument"
          :key="`highlight-card${highlightOrComment.highlightCard.highlightId}`"
          :championships="championships"
          :raceList="raceList"
          :highlight="highlightOrComment.highlightCard"
          :retrieveNameUsersByUserId="retrieveNameUsersByUserId"
        />
        <scene-list-card-parts
          v-else
          :key="`highlight-comment-card${highlightOrComment.highlightCard.id}`"
          class="highlights-scene-section__scene-item"
          :championships="championships"
          :raceList="raceList"
          :highlightComment="highlightOrComment.highlightCard"
          :highlightAdditional="highlightOrComment.newArrivalAdditionalData"
          :highlightsById="highlightsById"
          :commentsByHighlightId="commentsByHighlightId"
          :retrieveNameUsersByUserId="retrieveNameUsersByUserId"
        />
      </template>
    </carousel-list-wrapper-parts>
  </section>
</template>

<script lang="ts">
import { defineComponent, Ref } from '@vue/composition-api'
import CarouselListWrapperParts from '@/components/HomePage/HomePane/parts/CarouselListWrapperParts.vue'
import SceneListCardParts from '@/components/HomePage/HomePane/parts/SceneListCardParts.vue'
import HighlightsCardParts from '@/components/HomePage/HomePane/parts/HighlightsCardParts.vue'
import StoreUtil from '@/store/StoreUtil'
import type { NewArrivalHighlightDataType } from '@/store/stores/pageStore/HomePage/HomePageStore'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import type { UserRetrieveNameResponseType } from '@/store/stores/collectionModule/documents/user/UserRetrieveNameDocument'
import HighlightCommentDocument from '@/store/stores/collectionModule/documents/highlight/HighlightCommentDocument'
import ChampionshipDocument from '@/store/stores/collectionModule/documents/championship/ChampionshipDocument'
import RaceDocument from '@/store/stores/collectionModule/documents/race/RaceDocument'

/**
 * HOME SFgo’s HIGHLIGHTS セクション
 */
export default defineComponent({
  name: 'HighlightsSceneSection',
  computed: {
    HighlightDocument() {
      return HighlightDocument
    },
  },
  components: {
    HighlightsCardParts,
    CarouselListWrapperParts,
    SceneListCardParts,
  },
  props: {
    enabledCarouselList: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const homePageStore = StoreUtil.useStore('HomePageStore')
    const {
      newArrivalHighlightAndCommentData,
      championships,
      raceList,
      retrieveNameUsersByUserId,
      highlightsById,
      commentsByHighlightId,
    } = homePageStore

    return {
      championships: championships as Ref<Array<ChampionshipDocument>>,
      raceList: raceList as Ref<Array<RaceDocument>>,
      newArrivalHighlightAndCommentData: newArrivalHighlightAndCommentData as Ref<
        Array<NewArrivalHighlightDataType>
      >,
      retrieveNameUsersByUserId: retrieveNameUsersByUserId as Ref<
        Record<string, UserRetrieveNameResponseType>
      >,
      highlightsById: highlightsById as Ref<Record<string, HighlightDocument>>,
      commentsByHighlightId: commentsByHighlightId as Ref<
        Record<string, Array<HighlightCommentDocument>>
      >,
    }
  },
})
</script>

<style lang="scss" scoped>
.highlights-scene-section {
  width: 100%;

  &__title {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    width: 100%;
    max-width: 768px;
    margin: 0 auto 18px;
    padding: 0 16px;
    font-family: $fontSignup;

    &__icon {
      width: 18px;
    }

    &__text {
      font-weight: 700;
      font-size: 16px;
      line-height: 1.25;
      letter-spacing: 0.02em;
      font-feature-settings: 'palt' on;
    }

    &__tag {
      display: flex;
      flex-flow: row nowrap;
      align-items: flex-start;
      padding: 4px 6px;
      font-weight: 500;
      font-size: 10px;
      line-height: 1.4;
      letter-spacing: 0.04em;
      font-feature-settings: 'palt' on;
      color: $white;
      border-radius: 2px;
      background: $black20;
    }
  }
}
</style>
